import { ReactElement, useEffect, useMemo } from 'react';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import {
  BreadcrumbHeading,
  KpiCardLarge,
  type KpiCardLargeProps,
  MenuIcon,
  Plus,
  PricingIcon,
  SaladIcon,
  SoftButton,
  VenueIcon,
} from '@weareopenr/component-library';
import styles from './Dashboard.module.scss';
import { PriceSchemesWidget } from 'src/pages/Dashboard/Widgets/PriceSchemesWidget';
import { MenusWidget } from 'src/pages/Dashboard/Widgets/MenusWidget';
import { UpcomingPublishesWidget } from 'src/pages/Dashboard/Widgets/UpcomingPublishesWidget';
import { RecentPublishesWidget } from 'src/pages/Dashboard/Widgets/RecentPublishesWidget';
import { Routes } from 'src/routes';
import { useNavigate } from 'react-router-dom';
import { useMenus } from 'src/hooks/useMenus';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes/usePriceSchemes';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { useItems } from 'src/hooks/useItems/useItems';
import { getSyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import dayjs from 'dayjs';
import { IEndpoint } from 'src/types/aggregator.types';

const getLastSyncTime = (endpoint?: IEndpoint): Date | null => {
  if (endpoint?.lastUpdated) {
    return new Date(endpoint.lastUpdated);
  }
  return null;
};

export const Dashboard = (): ReactElement => {
  const { trackEvent } = useUserTracking();
  const navigate = useNavigate();
  const { data: menus, isLoading: menusLoading } = useMenus();
  const { data: venues, isLoading: venuesLoading } = useVenues();
  const { data: priceSchemes, isLoading: priceSchemesLoading } = usePriceSchemes();
  const { data: externalSystems, isLoading: externalSystemsLoading } = useExternalSystems();
  const { data: items, isLoading: itemsLoading } = useItems(undefined, 1);

  const syncEndpoint = useMemo(() => getSyncEndpoint(externalSystems), [externalSystems]);
  const lastSync = getLastSyncTime(syncEndpoint ?? undefined);
  const hasProducts = useMemo(() => (items?.pages ?? []).some((page) => page.length > 0), [items]);
  const lastSyncLabel = lastSync ? dayjs(lastSync).format('DD/MM/YY HH:mm') : 'N/A';

  useEffect(() => {
    document.title = 'Openr Hub';
    trackEvent('page_viewed', { page: 'dashboard' });
  }, []);

  const kpiCards: KpiCardLargeProps[] = [
    {
      id: 'products',
      size: 'large',
      icon: SaladIcon,
      label: 'Last Product Sync',
      value: hasProducts ? lastSyncLabel : '',
      testId: 'kpi-products',
      to: Routes.PRODUCT_LIBRARY,
      linkLabel: 'View Products',
      isLoading: itemsLoading || externalSystemsLoading,
      emptyCTA: (
        <SoftButton icon={Plus} size="full-width" onClick={() => navigate(Routes.PRODUCT_LIBRARY)}>
          Create a product
        </SoftButton>
      ),
    },
    {
      id: 'menus',
      size: 'large',
      icon: MenuIcon,
      label: 'Menus',
      value: menus?.length ?? 0,
      testId: 'kpi-menus',
      to: Routes.MENUS,
      linkLabel: 'View Menus',
      isLoading: menusLoading,
      emptyCTA: (
        <SoftButton icon={Plus} size="full-width" onClick={() => navigate(Routes.MENUS)}>
          Create a menu
        </SoftButton>
      ),
    },
    {
      id: 'price-schemes-in-use',
      size: 'large',
      icon: PricingIcon,
      label: 'Price Schemes',
      value: priceSchemes?.length ?? 0,
      testId: 'kpi-price-schemes-in-use',
      to: Routes.PRICE_SCHEMES,
      linkLabel: 'View Price Schemes',
      isLoading: priceSchemesLoading,
      emptyCTA: (
        <SoftButton icon={Plus} size="full-width" onClick={() => navigate(Routes.PRICE_SCHEMES)}>
          Create a price scheme
        </SoftButton>
      ),
    },
    {
      id: 'venues',
      size: 'large',
      icon: VenueIcon,
      label: 'Venues',
      value: venues?.length ?? 0,
      testId: 'kpi-venues',
      to: Routes.VENUES,
      linkLabel: 'View Venues',
      isLoading: venuesLoading,
      emptyCTA: (
        <SoftButton icon={Plus} size="full-width" onClick={() => navigate(Routes.VENUES)}>
          Create a venue
        </SoftButton>
      ),
    },
  ];

  return (
    <div data-testid="Wrapper" id="Wrapper" className={styles.wrapper}>
      <BreadcrumbHeading breadcrumbs={[{ label: 'Welcome Back', path: '/' }]} />
      <div className={styles['kpi-grid']}>
        {kpiCards.map((card) => (
          <KpiCardLarge key={card.id} {...card} />
        ))}
      </div>
      <div className={styles['widget-grid']}>
        <RecentPublishesWidget />
        <UpcomingPublishesWidget />
        <MenusWidget />
        <PriceSchemesWidget />
      </div>
    </div>
  );
};
